import { AABB3, MeasureEntity, MeasureSettings } from "@novorender/api";
import { ObjectId } from "@novorender/data-js-api";
import type { ReadonlyVec3, ReadonlyVec4 } from "gl-matrix";

export enum AsyncStatus {
    Initial,
    Loading,
    Success,
    Error,
}

export type AsyncEmpty = { status: Exclude<AsyncStatus, AsyncStatus.Success | AsyncStatus.Error> };
export type AsyncError = { status: AsyncStatus.Error; msg: string; stack?: string };
export type AsyncSuccess<T> = { status: AsyncStatus.Success; data: T };
export type AsyncState<T> = AsyncEmpty | AsyncError | AsyncSuccess<T>;

export function hasFinished<T>(state: AsyncState<T>): state is AsyncError | AsyncSuccess<T> {
    return state.status === AsyncStatus.Success || state.status === AsyncStatus.Error;
}

export function getAsyncStateData<T>(state: AsyncState<T>): T | undefined {
    if (state.status !== AsyncStatus.Success) {
        return;
    }

    return state.data;
}

export type ExtendedMeasureEntity = MeasureEntity & {
    settings?: MeasureSettings;
    connectionPoint?: ReadonlyVec3;
    isGenerated?: boolean;
};

export enum ViewMode {
    Default = "default",
    FollowPath = "followPath",
    Deviations = "deviations",
    CrossSection = "crossSection",
    Panorama = "panorama",
}

export type RecursivePartial<T> = { [P in keyof T]?: RecursivePartial<T[P]> };

// the NodeType in api does not work with --isolatedModules
export const NodeType = {
    Internal: 0,
    Leaf: 1,
};

// Redux toolkit with immer removes readonly modifier of state in the reducer so we get ts errors
// unless we cast the types to writable ones.
export type DeepMutable<T> = { -readonly [P in keyof T]: DeepMutable<T[P]> };

// Color expressed as a 4D vector with ranges [0.0 - 1.0] for red, green, blue and alpha components respectively.
export type ColorRGBA = ReadonlyVec4;

export type Color = {
    color: string;
    opacity: number;
};

export type Palette = {
    default?: Color;
    xray?: Color;
    selected?: Color;
};

export type PublicId = string;
export type PublicIdMap = Record<ObjectId, PublicId>;
export type ResolvedPublicIds = Record<PublicId, ObjectId>;
export type ResolvedIdMap = Record<ObjectId, ObjectId | undefined>;

export type CachedObject = {
    internalId: ObjectId;
    publicId: PublicId;
    /** Not loaded (e.g. resolve-only path). Use `PUBLIC_ID_NULL_VALUE` when the legacy id is known empty (search path). */
    oldPublicId?: PublicId;
    /**
     * - `undefined` — not loaded (including resolve: no bbox in response, so unknown if the object has bounds)
     * - `null` — loaded, known no bbox
     * - `AABB3` — loaded bounds
     */
    bbox?: AABB3 | null;
};

export type PaletteParams = {
    palette: Palette;
    publicIds: PublicId[];
};

export type InternalPaletteParams = {
    palette: Palette;
    objectIds: ObjectId[];
};
